import { IonApp, IonRouterOutlet, IonSplitPane, IonButton, IonToast, useBackButton, useIonRouter, isPlatform, } from '@ionic/vue';
import { defineComponent, computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { Auth } from '@/services/AuthService';
import { useStore } from 'vuex';
import { AuthActions } from "ionic-appauth";
import { useRouter } from "vue-router";
import AppMenu from "@/components/app-structure/AppMenu.vue";
import { App } from "@capacitor/app";
import PushNotificationActionHandler from "@/helpers/push/PushNotificationActionHandler";
import { Keyboard } from '@capacitor/keyboard';
import QRCodeScannerModal from "@/components/QRCodeScannerModal.vue";
import HelpModal from "@/components/HelpModal.vue";
import * as Sentry from '@sentry/capacitor';
import { filter, switchMap } from 'rxjs/operators';
export default defineComponent({
    name: 'App',
    setup() {
        const store = useStore();
        const router = useRouter();
        const subs = [];
        const splitPaneVisible = ref(false);
        const isScannerRunning = ref(false);
        const splitPaneDisabled = computed(() => {
            const routeName = String(router.currentRoute.value.name);
            const disabledOnRoutes = ['CampusApp', 'ClickAndLearnApp', 'ELearningAppPage'];
            return disabledOnRoutes.includes(routeName);
        });
        const visibleStateChange = (e) => {
            splitPaneVisible.value = e.detail.visible;
        };
        Auth.Instance.initComplete$.pipe(filter(complete => complete), switchMap(() => Auth.Instance.isAuthenticated$))
            .subscribe(async (isAuthenticated) => {
            store.commit('setAuthState', isAuthenticated);
        });
        subs.push(Auth.Instance.events$.subscribe((action) => {
            switch (action.action) {
                case AuthActions.SignOutSuccess:
                    console.log('SignOutSuccess');
                    Auth.clearUserData();
                    router.push('/');
                    break;
                case AuthActions.RefreshFailed:
                    console.log('RefreshFailed');
                    Auth.clearUserData();
                    router.push({ name: 'Landing' });
                    break;
                case AuthActions.LoadUserInfoSuccess: {
                    const cloudAPIURL = action.user.CloudApiUri || null;
                    store.commit('setUserInfo', {
                        loaded: true,
                        name: action.user.name,
                        username: action.user.preferred_username,
                        email: action.user.email,
                        cloudApiUrl: cloudAPIURL,
                    });
                    if (cloudAPIURL) {
                        localStorage.setItem('cloudApiUrl', cloudAPIURL);
                    }
                    else {
                        localStorage.removeItem('cloudApiUrl');
                    }
                    Sentry.setUser({ name: action.user.name });
                }
            }
        }));
        const ionRouter = useIonRouter();
        useBackButton(-1, () => {
            if (!ionRouter.canGoBack())
                App.exitApp();
        });
        const toastMessage = computed(() => store.state.toastMessage);
        const toastButtons = [{
                text: 'Öffnen',
                handler: () => {
                    if (toastMessage.value.action && toastMessage.value.args) {
                        const args = JSON.parse(toastMessage.value.args);
                        const handler = new PushNotificationActionHandler(toastMessage.value.action, args);
                        handler.run();
                    }
                }
            }];
        const qrScanner = computed(() => store.state.QRScanner);
        const qrcodeModal = ref(null);
        watch(() => store.state.QRScanner.show, async (value) => {
            if (value) {
                qrcodeModal.value?.open();
            }
            else {
                qrcodeModal.value?.close();
            }
        });
        const helpModal = ref(null);
        watch(() => store.state.showHelpModal, async (value) => {
            if (value) {
                helpModal.value?.open();
            }
            else {
                helpModal.value?.close();
            }
        });
        const mediaQueryForPrefersColorScheme = window.matchMedia('(prefers-color-scheme: dark)');
        const setIonPaletteDark = (value) => {
            document.documentElement.classList.toggle('ion-palette-dark', value);
        };
        const setIonPaletteDarkFromEvent = (event) => setIonPaletteDark(event.matches);
        watch(() => store.state.themePreference, async (value) => {
            if (value === 'system') {
                mediaQueryForPrefersColorScheme.addEventListener("change", setIonPaletteDarkFromEvent);
            }
            else {
                mediaQueryForPrefersColorScheme.removeEventListener("change", setIonPaletteDarkFromEvent);
            }
        });
        onMounted(() => {
            if (isPlatform('capacitor')) {
                Keyboard.addListener('keyboardDidShow', () => {
                    const webviewIframeEl = document.getElementById('webviewIframe');
                    const contentWindow = webviewIframeEl?.contentWindow;
                    if (contentWindow !== null) {
                        contentWindow.postMessage({ action: 'keyboardDidShow' }, '*');
                    }
                });
            }
            if (store.state.themePreference === 'system') {
                mediaQueryForPrefersColorScheme.addEventListener("change", setIonPaletteDarkFromEvent);
            }
            const isSystemThemePreferenceDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
            const ionPaletteDark = store.state.themePreference === 'system' ? isSystemThemePreferenceDark : store.state.themePreference === 'dark';
            setIonPaletteDark(ionPaletteDark);
        });
        // Move beforeUnmount logic to setup
        onBeforeUnmount(() => {
            subs.forEach(sub => sub.unsubscribe());
        });
        // Move computed property to setup
        const layout = computed(() => router.currentRoute.value.meta.layout || 'layout-main');
        return {
            store,
            subs,
            splitPaneDisabled,
            splitPaneVisible,
            visibleStateChange,
            toastMessage,
            toastButtons,
            qrScanner,
            isScannerRunning,
            qrcodeModal,
            helpModal,
            layout
        };
    },
    components: {
        QRCodeScannerModal,
        AppMenu,
        IonApp,
        IonRouterOutlet,
        IonSplitPane,
        IonButton,
        IonToast,
        HelpModal
    }
});
